import { useCallback, useEffect, useState } from 'react';
import client from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from './use-toast';

/**
 * Kanalı üyenin seçtiği 2FA'yı açıp kapatma akışı.
 *
 * Hem üye hesap sayfası hem de yönetim paneli aynı uçları kullandığı için
 * ortak tutulur: `request(action)` doğrulamayı başlatır, `confirm()` kodu
 * doğrulayarak durumu değiştirir. Kanal yalnızca açarken seçilir; kapatma
 * 2FA'nın hâlihazırda kullanıldığı kanaldan doğrulanır.
 */
export const useTwoFactorSettings = () => {
  const { refresh } = useAuth();
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [error, setError] = useState('');
  const [pending, setPending] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [channel, setChannel] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const { data } = await client.get('/auth/2fa');
      setStatus(data);
      // Kayıtlı tercih varsa o, yoksa kullanılabilir ilk kanal önerilir;
      // üyenin ekranda yaptığı seçim yeniden yüklemede korunur.
      setChannel((current) => current
        || data.channel
        || (data.channels || []).find((item) => item.channel === 'totp' && item.available)?.channel
        || (data.channels || []).find((item) => item.available)?.channel
        || '');
    } catch (e) {
      setError(e.response?.data?.detail || 'Güvenlik ayarları yüklenemedi');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const request = async (action) => {
    setBusy(true);
    try {
      const { data } = await client.post('/auth/2fa/request', {
        action,
        ...(action === 'enable' && channel ? { channel } : {}),
      });
      setPending({
        action,
        challenge: data.challenge,
        deliveryChannel: data.delivery_channel,
        deliveryTarget: data.delivery_target || data.phone_masked || data.email_masked,
        setupSecret: data.setup_secret,
        setupQr: data.setup_qr,
      });
      setCode('');
      if (data.delivery_channel !== 'totp') toast({
        title: 'Doğrulama kodu gönderildi',
        description: `${data.delivery_target || data.phone_masked || data.email_masked} hedefine gönderilen kodu girin`,
      });
    } catch (e) {
      toast({ title: 'Doğrulama başlatılamadı', description: e.response?.data?.detail || 'Bir hata oluştu' });
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!pending) return;
    setBusy(true);
    try {
      const { data } = await client.post('/auth/2fa/confirm', {
        action: pending.action,
        challenge: pending.challenge,
        code,
      });
      setPending(null);
      setCode('');
      await refresh(data.token);
      await load();
      toast({
        title: data.enabled ? 'İki adımlı doğrulama açıldı' : 'İki adımlı doğrulama kapatıldı',
        description: data.enabled
          ? pending.deliveryChannel === 'totp'
            ? 'Bundan sonra her girişte Google Authenticator uygulamanızdaki kod istenecek.'
            : 'Bundan sonra her girişte seçtiğiniz kanala doğrulama kodu gönderilecek.'
          : 'Girişlerde artık kod istenmeyecek',
      });
    } catch (e) {
      toast({ title: 'Doğrulanamadı', description: e.response?.data?.detail || 'Kod hatalı' });
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    if (!pending || busy) return;
    setBusy(true);
    try {
      await client.post('/auth/2fa/cancel', { challenge: pending.challenge });
      setPending(null);
      setCode('');
    } catch (e) {
      toast({ title: 'İşlem iptal edilemedi', description: e.response?.data?.detail || 'Lütfen yeniden deneyin.' });
    } finally {
      setBusy(false);
    }
  };

  return {
    status, error, pending, code, setCode, busy, load, request, confirm, cancel,
    channel, setChannel,
  };
};

export default useTwoFactorSettings;
